//src\redux\services\store\storeAuthApi.ts
import type { EndpointBuilder } from "@reduxjs/toolkit/query";
import { baseApi } from "../baseApi";

export type StoreFirebaseAuthRequest = {
  id_token: string;
  device_type?: string;
  device_name?: string;
};

export type StoreCustomerLoginRequest = {
  email?: string;
  mobile_e164?: string;
  password: string;
};

export type TokenResponse = {
  access_token: string;
  refresh_token: string;
  access_expires_in_minutes: number;
  refresh_expires_in_days: number;
};

export type StoreMeResponse = {
  id: number;
  email?: string | null;
  mobile_e164?: string | null;
  role: string;
  session_id: number;
  is_active: boolean;
  is_blocked: boolean;
  is_email_verified: boolean;
  is_mobile_verified: boolean;
  has_password: boolean;
};

export type MessageResponse = {
  message: string;
};

export type StoreSetPasswordRequest = {
  new_password: string;
  confirm_password: string;
};

export type StoreChangePasswordRequest = {
  old_password: string;
  new_password: string;
  confirm_password: string;
};

export type StoreForgotPasswordRequest = {
  identifier: string;
};

export type StoreResetPasswordConfirmRequest = {
  identifier: string;
  otp_code: string;
  new_password: string;
  confirm_password: string;
};

export type StoreResetPasswordMobileFirebaseRequest = {
  id_token: string;
  mobile_e164?: string | null;
  new_password: string;
  confirm_password: string;
};

export type StoreRequestMobileVerifyRequest = {
  mobile_e164: string;
};

export type StoreConfirmMobileVerifyRequest = {
  mobile_e164: string;
  otp_code: string;
};

export type StoreConfirmMobileFirebaseRequest = {
  id_token: string;
  mobile_e164?: string | null;
};

export type StoreRequestEmailVerifyRequest = {
  email: string;
};

export type StoreConfirmEmailVerifyRequest = {
  email: string;
  otp_code: string;
};

export type StoreVerifyRequestResponse = {
  message: string;
  purpose: string;
  channel: string;
  destination_value: string;
  expires_in_seconds: number;
  resend_after_seconds: number;
  otp_code?: string | null;
};

type Builder = EndpointBuilder<any, any, any>;

export const storeAuthApi = baseApi.injectEndpoints({
  endpoints: (builder: Builder) => ({
    firebaseStoreAuth: builder.mutation<TokenResponse, StoreFirebaseAuthRequest>({
      query: (body) => ({
        url: `/store/auth/firebase`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["StoreMe", "StoreCart", "StoreWishlist"],
    }),

    storeLogin: builder.mutation<TokenResponse, StoreCustomerLoginRequest>({
      query: (body) => ({
        url: `/store/auth/login`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["StoreMe", "StoreCart", "StoreWishlist"],
    }),

    getStoreMe: builder.query<StoreMeResponse, void>({
      query: () => ({
        url: `/store/auth/me`,
        method: "GET",
      }),
      providesTags: ["StoreMe"],
    }),

    setStorePassword: builder.mutation<MessageResponse, StoreSetPasswordRequest>({
      query: (body) => ({
        url: `/store/auth/set-password`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["StoreMe"],
    }),

    changeStorePassword: builder.mutation<MessageResponse, StoreChangePasswordRequest>({
      query: (body) => ({
        url: `/store/auth/change-password`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["StoreMe"],
    }),

    requestStoreForgotPassword: builder.mutation<
      StoreVerifyRequestResponse,
      StoreForgotPasswordRequest
    >({
      query: (body) => ({
        url: `/store/auth/forgot-password`,
        method: "POST",
        body,
      }),
    }),

    confirmStoreResetPassword: builder.mutation<
      MessageResponse,
      StoreResetPasswordConfirmRequest
    >({
      query: (body) => ({
        url: `/store/auth/reset-password`,
        method: "POST",
        body,
      }),
    }),

    confirmStoreResetPasswordWithFirebaseMobile: builder.mutation<
      MessageResponse,
      StoreResetPasswordMobileFirebaseRequest
    >({
      query: (body) => ({
        url: `/store/auth/reset-password/firebase-mobile`,
        method: "POST",
        body,
      }),
    }),

    requestStoreMobileVerify: builder.mutation<
      StoreVerifyRequestResponse,
      StoreRequestMobileVerifyRequest
    >({
      query: (body) => ({
        url: `/store/auth/mobile/request-verify`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["StoreMe"],
    }),

    confirmStoreMobileVerify: builder.mutation<
      MessageResponse,
      StoreConfirmMobileVerifyRequest
    >({
      query: (body) => ({
        url: `/store/auth/mobile/confirm-verify`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["StoreMe"],
    }),

    confirmStoreMobileVerifyWithFirebase: builder.mutation<
      MessageResponse,
      StoreConfirmMobileFirebaseRequest
    >({
      query: (body) => ({
        url: `/store/auth/mobile/confirm-firebase`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["StoreMe"],
    }),

    requestStoreEmailVerify: builder.mutation<
      StoreVerifyRequestResponse,
      StoreRequestEmailVerifyRequest
    >({
      query: (body) => ({
        url: `/store/auth/email/request-verify`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["StoreMe"],
    }),

    confirmStoreEmailVerify: builder.mutation<
      MessageResponse,
      StoreConfirmEmailVerifyRequest
    >({
      query: (body) => ({
        url: `/store/auth/email/confirm-verify`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["StoreMe"],
    }),

    storeLogout: builder.mutation<MessageResponse, void>({
      query: () => ({
        url: `/store/auth/logout`,
        method: "POST",
      }),
      invalidatesTags: ["StoreMe", "StoreCart", "StoreWishlist"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useFirebaseStoreAuthMutation,
  useStoreLoginMutation,
  useGetStoreMeQuery,
  useSetStorePasswordMutation,
  useChangeStorePasswordMutation,
  useRequestStoreForgotPasswordMutation,
  useConfirmStoreResetPasswordMutation,
  useConfirmStoreResetPasswordWithFirebaseMobileMutation,
  useRequestStoreMobileVerifyMutation,
  useConfirmStoreMobileVerifyMutation,
  useConfirmStoreMobileVerifyWithFirebaseMutation,
  useRequestStoreEmailVerifyMutation,
  useConfirmStoreEmailVerifyMutation,
  useStoreLogoutMutation,
} = storeAuthApi;
